import http from 'hub-http/clients/apiClient';
import { userInfoSync } from 'hub-http/userInfo';
import { makeSimpleQuickFetchWrapper } from '../../utils/ajaxUtils';
import { isUngatedForKnowledgeBaseV3_1 } from '../../utils/gatesUtils';
const reportFetcher = makeSimpleQuickFetchWrapper('migration-report', () => http.get('/knowledge-content/v2/migration/report'));
const articleCache = {};
let responseCache = null;
export const knowledgeBaseMigrationReport = () => {
  if (responseCache) {
    return Promise.resolve(responseCache);
  }
  return reportFetcher().then(response => {
    responseCache = response;
    return response;
  });
};
export const refetchCache = () => reportFetcher().then(response => {
  responseCache = response;
  return response;
});
export const knowledgeBaseMigrationReportSync = () => {
  if (!responseCache) throw new Error('knowledgeBaseMigrationReport must be called first');
  return responseCache;
};
const getV3KBs = () => {
  const {
    new: newKBs,
    migrated
  } = knowledgeBaseMigrationReportSync();
  return [...newKBs.map(({
    knowledgeBase
  }) => knowledgeBase), ...migrated.map(({
    scpKnowledgeBase
  }) => scpKnowledgeBase)];
};
const getKnowledgeBaseIdIsOnSCPCache = {};
export const getKnowledgeBaseIdIsOnSCP = knowledgeBaseId => {
  if (!isUngatedForKnowledgeBaseV3_1(userInfoSync().gates)) return false;
  if (typeof knowledgeBaseId === 'string') knowledgeBaseId = parseInt(knowledgeBaseId, 10);
  if (Object.hasOwn(getKnowledgeBaseIdIsOnSCPCache, knowledgeBaseId)) return getKnowledgeBaseIdIsOnSCPCache[knowledgeBaseId];
  const result = getV3KBs().some(kb => kb.values.hs_id === knowledgeBaseId);
  getKnowledgeBaseIdIsOnSCPCache[knowledgeBaseId] = result;
  return result;
};
export const getPrimaryKBIdForArticleId = articleId => {
  const article = articleCache[articleId];
  if (!article) throw new Error('article not found in cache');
  if (!responseCache) throw new Error('knowledgeBaseMigrationReport must be called first');
  if (getV3KBs().find(primaryKb => primaryKb.values.hs_id === article.values.hs_group_id)) {
    return article.values.hs_group_id;
  } else {
    const maybePrimaryKB = getV3KBs().find(primaryKb => Boolean(Object.values(primaryKb.translations || {}).find(translatedChildKB => translatedChildKB.values.hs_id === article.values.hs_group_id)));
    if (!maybePrimaryKB) throw new Error('could not find primary KB for article');
    return maybePrimaryKB.values.hs_id;
  }
};
export const getTranslatedKBIdForLanguage = (primaryKnowledgeBaseId, language) => {
  if (!responseCache) throw new Error('knowledgeBaseMigrationReport must be called first');
  const maybePrimaryKB = getV3KBs().find(primaryKb => primaryKb.values.hs_id === primaryKnowledgeBaseId);
  if (!maybePrimaryKB) throw new Error('could not find primary KB');
  if (maybePrimaryKB.values.hs_language === language) return primaryKnowledgeBaseId;
  const translations = maybePrimaryKB.translations;
  if (!translations || !language || !translations[language]) throw new Error(`could not find translation for ${language} on primary KB`);
  return translations[language].values.hs_id;
};
export const getPrimaryKBIdByLanguage = language => {
  if (!responseCache) throw new Error('knowledgeBaseMigrationReport must be called first');
  const primaryKB = getV3KBs().find(primaryKb => primaryKb.values.hs_language === language);
  if (primaryKB) {
    return primaryKB.values.hs_id;
  } else {
    const maybePrimaryKB = getV3KBs().find(kb => Boolean(kb.translations && kb.translations[language]));
    if (!maybePrimaryKB) throw new Error('could not find primary KB for article');
    return maybePrimaryKB.values.hs_id;
  }
};
export const getTranslatedKBIdForTranslatedFromArticleId = (articleId, lang) => {
  const primaryKBId = getPrimaryKBIdForArticleId(articleId);
  if (!responseCache) throw new Error('knowledgeBaseMigrationReport must be called first');
  const primaryKB = getV3KBs().find(primaryKb => primaryKb.values.hs_id === primaryKBId);
  if (!primaryKB) throw new Error('could not find primary KB for article');
  const translatedKB = primaryKB.translations && primaryKB.translations[lang];
  if (!translatedKB) throw new Error('could not find translated KB for language');
  return translatedKB.values.hs_id;
};
export const addSCPArticlesToMigrationCache = articles => {
  articles.forEach(article => {
    const translations = Object.values(article.translations);
    articleCache[article.values.hs_id] = article;
    if (translations.length > 0) {
      translations.forEach(translatedArticle => {
        articleCache[translatedArticle.values.hs_id] = translatedArticle;
      });
    }
  });
};
const getArticleHasBeenVerifiedToBeOnSCP = articleId => {
  if (Object.hasOwn(articleCache, articleId)) {
    return articleCache[articleId];
  }
  return null;
};
export const getShouldCallArticleACPEndpoints = articleId => {
  return isUngatedForKnowledgeBaseV3_1(userInfoSync().gates) && articleId && getArticleHasBeenVerifiedToBeOnSCP(articleId);
};
export const getShouldCallSCPEndpoints = knowledgeBaseId => {
  return isUngatedForKnowledgeBaseV3_1(userInfoSync().gates) && knowledgeBaseId && getKnowledgeBaseIdIsOnSCP(knowledgeBaseId);
};
export const getHasKBv3KB = () => {
  return isUngatedForKnowledgeBaseV3_1(userInfoSync().gates) && knowledgeBaseMigrationReportSync().new.length > 0;
};
export const getFirstKBv3KB = () => {
  return isUngatedForKnowledgeBaseV3_1(userInfoSync().gates) ? knowledgeBaseMigrationReportSync().new[0].knowledgeBase : null;
};
export const convertTranslationsToOldFormat = category => {
  const translations = category.translations;
  const translationList = [];
  //adds primary language to translations
  translationList.push({
    name: category.name,
    description: category.description || '',
    language: category.language,
    id: category.id
  });
  for (const [language, {
    name,
    description,
    id
  }] of Object.entries(translations)) {
    translationList.push({
      name,
      description,
      language,
      id
    });
  }
  return translationList;
};
export const convertCategoriesToOldFormat = categories => {
  const oldCategories = categories.map(category => ({
    id: category.id,
    isPublished: category.state === 'PUBLISHED',
    isDisplayed: category.isDisplayed,
    position: category.position,
    state: category.state,
    articles: category.articles,
    language: category.language,
    subcategories: category.subcategories.length > 0 ? convertCategoriesToOldFormat(category.subcategories) : [],
    translatedToLanguages: [category.language, ...Object.keys(category.translations)],
    translations: convertTranslationsToOldFormat(category)
  }));
  return oldCategories;
};
export const convertPrimaryCategoriesToOldFormat = categories => {
  const oldCategories = categories.map(category => ({
    id: category.id,
    isPublished: category.state === 'PUBLISHED',
    isDisplayed: category.isDisplayed,
    position: category.position,
    state: category.state,
    totalPublishedArticles: category.totalPublishedArticles,
    totalArticles: category.totalArticles,
    totalSubcategories: category.totalSubcategories,
    language: category.language,
    articles: [],
    subcategories: [],
    translatedToLanguages: [category.language, ...Object.keys(category.translations)],
    translations: convertTranslationsToOldFormat(category),
    convertedToLegacy: true
  }));
  return oldCategories;
};
export const convertMultiLangCategoriesToOldFormat = categories => {
  const oldCategories = categories.map(category => {
    return {
      id: category.id,
      knowledgeCategoryId: category.id,
      name: category.name,
      language: category.language,
      nameTranslations: convertTranslationsToOldFormat(category),
      subcategories: category.subcategories && convertMultiLangCategoriesToOldFormat(category.subcategories)
    };
  });
  return oldCategories;
};
export const convertMultiLangEditResponseToOldFormat = categories => {
  const oldCategories = categories.map(category => {
    return {
      id: category.id,
      knowledgeCategoryId: category.id,
      name: category.name,
      language: category.language,
      nameTranslations: convertTranslationsToOldFormat(category)
    };
  });
  return oldCategories;
};